import { Box, Button, Flex, FormControl, FormLabel, Heading, HStack, Icon, Input, Radio, RadioGroup, Select, SimpleGrid, Stack, Text, VStack, Image, useToast, Alert, AlertIcon, AlertDescription, Checkbox } from '@chakra-ui/react';
import axios from 'axios';

import React, { useState } from 'react'
import { LuMessageCircle, LuFileText, LuFileSpreadsheet, LuFile, LuPlay, LuBell, LuUploadCloud, LuX } from 'react-icons/lu';
import ReactSelect from "react-select";

const Whatsapp = ({ formData, updateField, data }) => {
    const [uploading, setUploading] = useState(false);
    const [headerVariable, setHeaderVariable] = useState({ name: '', description: '' });
    const [headerVariableError, setHeaderVariableError] = useState('');
    const toast = useToast();

    const addWhatsAppButton = () => {
        const newButton = {
            id: `btn_${Date.now()}`,
            type: "custom-link",
            name: "",
            urlType: "static",
            url: "",
            phoneNumber: ""
        };
        updateField('whatsappButtons', [...formData.whatsappButtons, newButton]);
    };

    const removeWhatsAppButton = (index) => {
        const updatedButtons = formData.whatsappButtons.filter((_, i) => i !== index);
        updateField('whatsappButtons', updatedButtons);
    };

    const updateWhatsAppButton = (index, field, value) => {
        const updatedButtons = formData.whatsappButtons.map((button, i) =>
            i === index ? { ...button, [field]: value } : button
        );
        updateField('whatsappButtons', updatedButtons);
    };

    // Detect variables in WhatsApp header text
    const detectHeaderVariables = (content) => {
        const variableRegex = /\$([a-zA-Z_][a-zA-Z0-9_]*)/g;
        const matches = [...content.matchAll(variableRegex)];
        const detectedVariables = matches.map(match => match[1]);

        // Check total count of variables first (including duplicates)
        if (detectedVariables.length > 1) {
            setHeaderVariableError('Only one variable is allowed in header text');
            // Don't update headerVariable if there's an error
            return null;
        } else if (detectedVariables.length === 1) {
            setHeaderVariableError('');
            const varName = detectedVariables[0];

            // Create or update the header variable
            const updatedHeaderVariable = {
                name: varName,
                // Preserve existing description if variable name hasn't changed, otherwise reset
                description: headerVariable.name === varName ? headerVariable.description : ''
            };

            setHeaderVariable(updatedHeaderVariable);

            // Update the headerVariable in formData as well
            updateField('headerVariable', updatedHeaderVariable);

            return updatedHeaderVariable;
        } else {
            setHeaderVariableError('');
            const emptyVariable = { name: '', description: '' };
            setHeaderVariable(emptyVariable);
            updateField('headerVariable', emptyVariable);
            return null;
        }
    };

    // Handle WhatsApp header text change
    const handleHeaderTextChange = (value) => {
        // Update the header content
        updateField('whatsappHeader', {
            ...formData.whatsappHeader,
            content: value
        });

        // Detect variables in the header text
        detectHeaderVariables(value);
    };

    // Update header variable description
    const updateHeaderVariableDescription = (description) => {
        const updatedVariable = {
            ...headerVariable,
            description
        };

        setHeaderVariable(updatedVariable);
        updateField('headerVariable', updatedVariable);
    };

    const uploadFile = async (file) => {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/template-message/media-upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "x-access-token": localStorage.getItem("user"),
                    },
                }
            );

            const result = response.data;

            if (result.success) {
                toast({
                    title: "Success",
                    description: result.message,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                return result.fileUrl;
            } else {
                throw new Error(result.message || "Upload failed");
            }
        } catch (error) {
            toast({
                title: "Upload Error",
                description: error.response?.data?.message || error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return null;
        } finally {
            setUploading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const fileUrl = await uploadFile(file);
        if (fileUrl) {
            updateField('whatsappHeader', {
                ...formData.whatsappHeader,
                file: `${fileUrl}`,
                fileUrl: `${fileUrl}`,
                fileName: file.name,
                fileType: file.type
            });
        }
    };

    const getFileIcon = (fileType, fileName) => {
        if (!fileType && !fileName) return LuFile;

        const type = fileType || '';
        const name = fileName || '';

        if (type.startsWith('image/')) {
            return null;
        } else if (type === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) {
            return LuFileText;
        } else if (type.includes('word') || name.toLowerCase().endsWith('.doc') || name.toLowerCase().endsWith('.docx')) {
            return LuFileText;
        } else if (type.includes('sheet') || name.toLowerCase().endsWith('.xls') || name.toLowerCase().endsWith('.xlsx')) {
            return LuFileSpreadsheet;
        } else if (type.includes('presentation') || name.toLowerCase().endsWith('.ppt') || name.toLowerCase().endsWith('.pptx')) {
            return LuFileText;
        } else if (type.startsWith('video/')) {
            return LuPlay;
        } else {
            return LuFile;
        }
    };

    const getFileIconColor = (fileType, fileName) => {
        if (!fileType && !fileName) return 'gray.500';

        const type = fileType || '';
        const name = fileName || '';

        if (type === 'application/pdf' || name.toLowerCase().endsWith('.pdf')) {
            return 'red.500';
        } else if (type.includes('word') || name.toLowerCase().endsWith('.doc') || name.toLowerCase().endsWith('.docx')) {
            return 'blue.500';
        } else if (type.includes('sheet') || name.toLowerCase().endsWith('.xls') || name.toLowerCase().endsWith('.xlsx')) {
            return 'green.500';
        } else if (type.includes('presentation') || name.toLowerCase().endsWith('.ppt') || name.toLowerCase().endsWith('.pptx')) {
            return 'orange.500';
        } else if (type.startsWith('video/')) {
            return 'purple.500';
        } else {
            return 'gray.500';
        }
    };

    const removeUploadedFile = () => {
        updateField('whatsappHeader', {
            ...formData.whatsappHeader,
            file: null,
            fileUrl: '',
            fileName: '',
            fileType: ''
        });
    };

    const renderFilePreview = () => {
        const { fileUrl, fileName, fileType, file } = formData.whatsappHeader;

        if (!fileUrl && !file) return null;

        const isImage = fileType?.startsWith('image/') || file?.type?.startsWith('image/');
        const FileIcon = getFileIcon(fileType || file?.type, fileName || file?.name);
        const iconColor = getFileIconColor(fileType || file?.type, fileName || file?.name);

        return (
            <Box
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                p={4}
                bg="gray.50"
                position="relative"
            >
                <Button
                    position="absolute"
                    top={2}
                    right={2}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={removeUploadedFile}
                    zIndex={2}
                    pointerEvents={data?.whatsapp_status_id === 1 ? 'none' : 'auto'}
                >
                    <Icon as={LuX} />
                </Button>

                {isImage ? (
                    <Flex direction="column" align="center" justify="center" py={4}>
                        <Image
                            src={fileUrl || (file ? URL.createObjectURL(file) : '')}
                            alt="Uploaded image"
                            maxH="200px"
                            maxW="100%"
                            objectFit="contain"
                            borderRadius="md"
                        />
                    </Flex>
                ) : (
                    <Flex direction="column" align="center" justify="center" py={4}>
                        {FileIcon && (
                            <Icon as={FileIcon} boxSize={12} color={iconColor} mb={2} />
                        )}
                        <Text fontSize="sm" fontWeight="medium" textAlign="center">
                            {fileName || file?.name || 'Uploaded File'}
                        </Text>
                        {(fileType || file?.type) && (
                            <Text fontSize="xs" color="gray.500">
                                {(fileType || file?.type)?.toUpperCase()}
                            </Text>
                        )}

                    </Flex>
                )}
            </Box>
        );
    };

    // Initialize headerVariable from formData if it exists
    React.useEffect(() => {
        if (formData.headerVariable && formData.headerVariable.name) {
            setHeaderVariable(formData.headerVariable);
        }
    }, [formData.headerVariable]);


    const expiryOptions = [
        { value: 0, label: 'Never expiry' },
        { value: 30000, label: '30 seconds' },
        { value: 60000, label: '1 minute' },
        { value: 120000, label: '2 minutes' },
        { value: 180000, label: '3 minutes' },
        { value: 300000, label: '5 minutes' },
        { value: 600000, label: '10 minutes' },
        { value: 900000, label: '15 minutes' }
    ];
    const languageOptions = [
        { value: "en_US", label: 'English US' },
        { value: "en", label: 'English' },
    ];



    const ExpirySection = () => {
        const handleExpiryToggle = (checked) => {
            updateField('isExpiryEnabled', checked);
            if (!checked) {
                updateField('expiry_time', 0);
            }
        };

        const handleExpiryTimeChange = (selectedOption) => {
            updateField('expiry_time', selectedOption?.value || 0);
        };
        const handleLanguageChange = (selectedOption) => {
            updateField('lang', selectedOption?.value);
        };

        return (


            <div >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px', // spacing between items

                        padding: '10px',
                        marginRight: '20px',
                    }}
                >
                    <Checkbox
                        isChecked={formData.isExpiryEnabled}
                        onChange={(e) => handleExpiryToggle(e.target.checked)}
                        disabled={data?.whatsapp_status_id === 1}
                    >
                        <span
                            style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#333',
                            }}
                        >
                            Enable Expiry
                        </span>
                    </Checkbox>

                    {formData.isExpiryEnabled && (
                        <FormControl style={{ width: '200px' }}>
                            <FormLabel>Expiry Time</FormLabel>
                            <ReactSelect
                                value={expiryOptions.find(
                                    (option) => option.value === formData.expiry_time
                                )}
                                onChange={handleExpiryTimeChange}
                                options={expiryOptions}
                                placeholder="Select expiry time"
                                isClearable={false}
                            />
                        </FormControl>
                    )}
                </div>
                <FormControl style={{ width: '200px' }}>
                    <FormLabel>Language</FormLabel>
                    <ReactSelect
                        value={languageOptions.find(
                            (option) => option.value === formData.lang
                        )}
                        onChange={handleLanguageChange}
                        options={languageOptions}
                        placeholder="Select Language"
                        isClearable={true}
                        isDisabled={data?.whatsapp_status_id === 1}
                    />
                </FormControl>
            </div>


        );
    };

    return (
        <div>
            {formData.selectedChannels.includes("Whatsapp") && (
                <Box p={6} className='box-style-card' >
                    <HStack mb={4} spacing={3}>
                        <Icon as={LuMessageCircle} color="green.500" boxSize={6} />
                        <Heading size="md" color="grey.700" fontWeight={400}>
                            WhatsApp Settings
                        </Heading>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mb={4}>
                        Configure WhatsApp-specific features. The master message content will be used as the main message.
                    </Text>

                    <VStack spacing={6} align="stretch">

                        {/* <FormControl>
                            <FormLabel>WhatsApp Template Name</FormLabel>
                            <Input
                                value={formData.whatsappTemplateName}
                                onChange={(e) => updateField('whatsappTemplateName', e.target.value)}
                                placeholder="e.g., otp_verification_v2"
                                bg="white"
                                disabled
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                This will be the registered template name in WhatsApp Business API
                            </Text>
                        </FormControl> */}

                        {+formData?.templateType !== 1 ? (
                            <>

                                <Box>
                                    <FormLabel>Header (Optional)</FormLabel>
                                    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2} mb={4}>
                                        {[
                                            { type: 'text', icon: '📝', label: 'Text' },
                                            { type: 'image', icon: '🖼️', label: 'Image' },
                                            { type: 'video', icon: '🎥', label: 'Video' },
                                            { type: 'document', icon: '📄', label: 'Document' },
                                            { type: 'location', icon: '📍', label: 'Location' }
                                        ].map((headerType) => (
                                            <Box
                                                key={headerType.type}
                                                p={1}
                                                border="1px"
                                                borderColor={formData.whatsappHeader.type === headerType.type ? '#2563eb' : 'gray.200'}
                                                borderRadius="md"
                                                bg={formData.whatsappHeader.type === headerType.type ? '#2563eb' : 'white'}
                                                cursor="pointer"
                                                textAlign="center"
                                                pointerEvents={data?.whatsapp_status_id === 1 ? 'none' : 'auto'}
                                                onClick={() => {
                                                    if (formData.whatsappHeader.type === headerType.type) {
                                                        updateField('whatsappHeader', {
                                                            type: '',
                                                            content: '',
                                                            file: null,
                                                            fileUrl: '',
                                                            fileName: '',
                                                            fileType: '',
                                                            longitude: '',
                                                            latitude: '',
                                                            location_address:"",
                                                            location_name:""
                                                        });
                                                        // Reset header variable when changing header type
                                                        setHeaderVariable({ name: '', description: '' });
                                                        setHeaderVariableError('');
                                                        updateField('headerVariable', { name: '', description: '' });
                                                    } else {
                                                        updateField('whatsappHeader', {
                                                            type: headerType.type,
                                                            content: '',
                                                            file: null,
                                                            fileUrl: '',
                                                            fileName: '',
                                                            fileType: '',
                                                            longitude: '',
                                                            latitude: '',
                                                            location_address:"",
                                                            location_name:""
                                                        });
                                                        // Reset header variable when changing header type
                                                        setHeaderVariable({ name: '', description: '' });
                                                        setHeaderVariableError('');
                                                        updateField('headerVariable', { name: '', description: '' });
                                                    }
                                                }}
                                                _hover={{ borderColor: '#2563eb' }}
                                            >
                                                <Text fontSize="xs" fontWeight="medium" color={formData.whatsappHeader.type === headerType.type ? 'white' : 'black'}>{headerType.label}</Text>
                                            </Box>
                                        ))}
                                    </SimpleGrid>

                                    {formData.whatsappHeader.type === 'text' && (
                                        <VStack spacing={4} align="stretch">
                                            <FormControl isInvalid={!!headerVariableError}>
                                                <FormLabel>Header Text</FormLabel>
                                                <Input
                                                    value={formData.whatsappHeader.content || ''}
                                                    onChange={(e) => handleHeaderTextChange(e.target.value)}
                                                    placeholder="Enter header text (use $variable_name for dynamic content)"
                                                    bg="white"
                                                    disabled={data?.whatsapp_status_id === 1}
                                                />
                                                {headerVariableError && (
                                                    <Alert status="error" mt={2}>
                                                        <AlertIcon />
                                                        <AlertDescription>{headerVariableError}</AlertDescription>
                                                    </Alert>
                                                )}
                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                    Use $variable_name syntax for dynamic content. Only one variable is allowed.
                                                </Text>
                                            </FormControl>

                                            {/* Header Variable Configuration */}
                                            {headerVariable.name && !headerVariableError && (
                                                <Box p={4} border="1px" borderColor="green.200" borderRadius="md" bg="green.50">
                                                    <HStack mb={3} spacing={2}>
                                                        <Icon as={LuBell} color="green.600" />
                                                        <Text fontSize="sm" fontWeight="medium" color="green.700">
                                                            Header Variable Detected
                                                        </Text>
                                                    </HStack>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm">Variable Name</FormLabel>
                                                            <span className='chip-variable'>${headerVariable.name}</span>
                                                        </FormControl>
                                                        <FormControl>
                                                            <FormLabel fontSize="sm">Variable Description</FormLabel>
                                                            <Input
                                                                value={headerVariable.description}
                                                                onChange={(e) => updateHeaderVariableDescription(e.target.value)}
                                                                placeholder={`Describe what ${headerVariable.name} represents...`}
                                                                bg="white"
                                                                size="sm"
                                                                disabled={data?.whatsapp_status_id === 1}
                                                            />
                                                        </FormControl>
                                                    </SimpleGrid>
                                                </Box>
                                            )}
                                        </VStack>
                                    )}

                                    {(['image', 'video', 'document'].includes(formData.whatsappHeader.type)) && (
                                        <FormControl>
                                            {(formData.whatsappHeader.fileUrl || formData.whatsappHeader.file) ? (
                                                renderFilePreview()
                                            ) : (
                                                <Box
                                                    border="2px dashed"
                                                    borderColor="gray.300"
                                                    borderRadius="md"
                                                    p={6}
                                                    textAlign="center"
                                                    cursor="pointer"
                                                    position="relative"
                                                    _hover={{ borderColor: "blue.400", bg: "gray.50" }}
                                                >
                                                    <Input
                                                        type="file"
                                                        accept={
                                                            formData.whatsappHeader.type === 'image' ? 'image/*' :
                                                                formData.whatsappHeader.type === 'video' ? 'video/*' : '*/*'
                                                        }
                                                        onChange={handleFileUpload}
                                                        bg="white"
                                                        p={2}
                                                        position="absolute"
                                                        top={0}
                                                        left={0}
                                                        w="100%"
                                                        h="100%"
                                                        opacity={0}
                                                        cursor="pointer"
                                                        disabled={uploading}
                                                    />

                                                    <Flex direction="column" align="center" justify="center">
                                                        <Icon as={LuUploadCloud} boxSize={6} color="gray.400" mb={2} />
                                                        <Text fontSize="sm" color="gray.600">
                                                            {uploading ? 'Uploading...' : `Click to upload ${formData.whatsappHeader.type}`}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.500" mt={1}>
                                                            {formData.whatsappHeader.type === 'image' ? 'PNG, JPG, GIF up to 10MB' :
                                                                formData.whatsappHeader.type === 'video' ? 'MP4, AVI up to 100MB' :
                                                                    'PDF, DOC, XLS, PPT up to 100MB'}
                                                        </Text>
                                                    </Flex>
                                                </Box>
                                            )}
                                        </FormControl>
                                    )}

                                    {formData.whatsappHeader.type === 'location' && (
                                        <VStack align="stretch" spacing={4}>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                <FormControl>
                                                    <FormLabel fontSize="sm">Longitude</FormLabel>
                                                    <Input
                                                        value={formData.whatsappHeader.longitude || ''}
                                                        onChange={(e) => updateField('whatsappHeader', {
                                                            ...formData.whatsappHeader,
                                                            longitude: e.target.value
                                                        })}
                                                        placeholder="e.g., -73.935242"
                                                        bg="white"
                                                        disabled={data?.whatsapp_status_id === 1}
                                                    />
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel fontSize="sm">Latitude</FormLabel>
                                                    <Input
                                                        value={formData.whatsappHeader.latitude || ''}
                                                        onChange={(e) => updateField('whatsappHeader', {
                                                            ...formData.whatsappHeader,
                                                            latitude: e.target.value
                                                        })}
                                                        placeholder="e.g., 40.730610"
                                                        bg="white"
                                                        disabled={data?.whatsapp_status_id === 1}
                                                    />
                                                </FormControl>
                                            </SimpleGrid>

                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                <FormControl>
                                                    <FormLabel fontSize="sm">Location Name</FormLabel>
                                                    <Input
                                                        value={formData.whatsappHeader.location_name || ''}
                                                        onChange={(e) => updateField('whatsappHeader', {
                                                            ...formData.whatsappHeader,
                                                            location_name: e.target.value
                                                        })}
                                                        placeholder="e.g., Central Park"
                                                        bg="white"
                                                        disabled={data?.whatsapp_status_id === 1}
                                                    />
                                                </FormControl>

                                                <FormControl>
                                                    <FormLabel fontSize="sm">Location Address</FormLabel>
                                                    <Input
                                                        value={formData.whatsappHeader.location_address || ''}
                                                        onChange={(e) => updateField('whatsappHeader', {
                                                            ...formData.whatsappHeader,
                                                            location_address: e.target.value
                                                        })}
                                                        placeholder="e.g., New York, NY, USA"
                                                        bg="white"
                                                        disabled={data?.whatsapp_status_id === 1}
                                                    />
                                                </FormControl>
                                            </SimpleGrid>
                                        </VStack>
                                    )}

                                </Box>

                                <FormControl>
                                    <FormLabel>Footer (Optional)</FormLabel>
                                    <Input
                                        value={formData.whatsappFooter}
                                        onChange={(e) => updateField('whatsappFooter', e.target.value)}
                                        placeholder="Enter footer text"
                                        bg="white"
                                        disabled={data?.whatsapp_status_id === 1}
                                    />
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        Footer text appears below WhatsApp message. {(formData.whatsappFooter || '').length}/60 characters
                                    </Text>
                                </FormControl>

                                <Box>
                                    <HStack justify="space-between" mb={3}>
                                        <Text fontWeight="medium">Interactive Buttons (Optional)</Text>
                                        <Button
                                            size="sm"
                                            colorScheme="green"
                                            variant="outline"
                                            onClick={addWhatsAppButton}
                                            isDisabled={formData.whatsappButtons.length >= 3 || data?.whatsapp_status_id === 1}
                                        >
                                            + Add Button ({formData.whatsappButtons.length}/3)
                                        </Button>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500" mb={4}>
                                        Add up to 3 interactive buttons
                                    </Text>

                                    {formData.whatsappButtons.map((button, index) => (
                                        <Box key={button.id} p={4} border="1px" borderColor="gray.200" borderRadius="md" mb={3} bg="white">
                                            <HStack justify="space-between" mb={3}>
                                                <Text fontSize="sm" fontWeight="medium">Button {index + 1}</Text>
                                                <Button
                                                    size="sm"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => removeWhatsAppButton(index)}
                                                    pointerEvents={data?.whatsapp_status_id === 1 ? "none" : "auto"}
                                                >
                                                    🗑️
                                                </Button>
                                            </HStack>

                                            <VStack spacing={3} align="stretch">
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                    <FormControl>
                                                        <FormLabel fontSize="sm">Button Text</FormLabel>
                                                        <Input
                                                            value={button.name || ''}
                                                            onChange={(e) => updateWhatsAppButton(index, 'name', e.target.value)}
                                                            placeholder="Enter button text"
                                                            size="sm"
                                                            maxLength={20}
                                                            disabled={data?.whatsapp_status_id === 1}
                                                        />
                                                        <Text fontSize="xs" color="gray.500">
                                                            {(button.name || '').length}/20 characters
                                                        </Text>
                                                    </FormControl>

                                                    <FormControl>
                                                        <FormLabel fontSize="sm">Button Type</FormLabel>
                                                        <Select
                                                            value={button.type || 'custom-link'}
                                                            onChange={(e) => updateWhatsAppButton(index, 'type', e.target.value)}
                                                            size="sm"
                                                            disabled={data?.whatsapp_status_id === 1}
                                                        >
                                                            <option value="custom-link">🔗 Custom Link</option>
                                                            <option value="call-me">📞 Call Me</option>
                                                        </Select>
                                                    </FormControl>
                                                </SimpleGrid>

                                                {button.type === 'custom-link' && (
                                                    <Box>
                                                        <FormLabel fontSize="sm">URL Type</FormLabel>
                                                        <RadioGroup
                                                            value={button.urlType || 'static'}
                                                            onChange={(value) => updateWhatsAppButton(index, 'urlType', value)}
                                                            mb={3}

                                                            isDisabled={data?.whatsapp_status_id === 1}
                                                        >
                                                            <Stack direction="row" spacing={4}>
                                                                <Radio value="static" colorScheme="green">
                                                                    🔒 Static URL
                                                                </Radio>
                                                                <Radio value="dynamic" colorScheme="green">
                                                                    ⚡ Dynamic URL (with variables)
                                                                </Radio>
                                                            </Stack>
                                                        </RadioGroup>

                                                        <FormControl>
                                                            <FormLabel fontSize="sm">URL Template</FormLabel>
                                                            <Input
                                                                value={button.url || ''}
                                                                onChange={(e) => updateWhatsAppButton(index, 'url', e.target.value)}
                                                                placeholder={
                                                                    button.urlType === 'dynamic'
                                                                        ? "https://example.com/verify?code={{1}}&user={{2}}"
                                                                        : "https://example.com"
                                                                }
                                                                disabled={data?.whatsapp_status_id === 1}
                                                                size="sm"
                                                            />
                                                            {button.urlType === 'dynamic' && (
                                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                                    Use {'{{'} 1 {'}}'}  and {'{{'} 2 {'}}'}  to wrap variable positions (e.g., {'{{'} 1 {'}}'}, {'{{'} 2 {'}}'}  )
                                                                </Text>
                                                            )}
                                                        </FormControl>
                                                    </Box>
                                                )}

                                                {button.type === 'call-me' && (
                                                    <FormControl>
                                                        <FormLabel fontSize="sm">Phone Number</FormLabel>
                                                        <Input
                                                            value={button.phoneNumber || ''}
                                                            onChange={(e) => updateWhatsAppButton(index, 'phoneNumber', e.target.value)}
                                                            placeholder="+1234567890"
                                                            size="sm"
                                                            disabled={data?.whatsapp_status_id === 1}
                                                        />
                                                    </FormControl>
                                                )}
                                            </VStack>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : ""}

                        <ExpirySection />

                    </VStack>
                </Box>
            )}
        </div>
    )
}

export default Whatsapp